import { amplifyService } from '@kasasa/kloans-common-ui/dist/consumer-app/services/amplify';
import { AuthenticatedHttpService } from '@/core/services/authenticated-http';
import { SuccessEnvelope } from '@/core/types/success-envelope';
import { ProfileAccount } from '@/profile/types/profile-account';
import { ProfileLinkedAccount } from '@/profile/types/profile-linked-account';
import { SessionService } from '@/core/services/session';
import { AccountType } from '@/core/enums/account-type';
import { ProfileBankAccount } from '@/profile/types/profile-bank-account';
import { ProfileAddBankAccountRequest } from '@/profile/types/profile-add-bank-account-request';
import { ProfileVerifyPhoneRequest } from '@/profile/types/profile-verify-phone-request';
import { ProfileChangePinRequest } from '@/profile/types/profile-change-pin-request';
import { UtilHelper } from '@/util/helper';

export class ProfileAPIService {
	public static acceptDwollaTerms(): Promise<any> {
		return AuthenticatedHttpService.post<SuccessEnvelope<any>>(
			'/api/kloans-consumer/dwolla-terms-accepted',
		);
	}

	public static addBankAccount(request: ProfileAddBankAccountRequest): Promise<ProfileBankAccount> {
		return AuthenticatedHttpService.post<SuccessEnvelope<any>, ProfileAddBankAccountRequest>(
			'/api/v1/bank-account',
			request,
		).then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	public static addLinkedAccounts(metaData: any): Promise<ProfileLinkedAccount[]> {
		return AuthenticatedHttpService
			.patch<SuccessEnvelope<ProfileLinkedAccount[]>>(
				'/api/kloans-iav/linked-accounts',
				metaData,
			).then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	public static async changePassword({
		oldPassword,
		newPassword,
	}: {
		oldPassword: string;
		newPassword: string;
	}): Promise<void> {
		// eslint-disable-next-line no-async-promise-executor
		const promise = new Promise<void>(async (resolve, reject) => {
			await amplifyService.changePassword(
				oldPassword,
				newPassword,
				{
					onFailure: (message: string) => {
						reject(message);
					},
					onSuccess: () => {
						resolve();
					},
				},
			);
		});

		return promise;
	}

	public static changePin(
		oldPin: string,
		pin: string,
		pinConfirmation: string,
	): Promise<{ success: true }> {
		return AuthenticatedHttpService.put<SuccessEnvelope<{ success: true }>, ProfileChangePinRequest>(
			'/api/kloans-consumer/pin',
			{ oldPin, pin, pinConfirmation },
		).then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	public static checkIsEmailAvailable(email: string): Promise<boolean> {
		return AuthenticatedHttpService.post<boolean, { email: string }>(
			'/api/kloans-consumer/validate/email-available',
			{ email },
		);
	}

	public static confirmEmailChange(nonce: string): Promise<any> {
		return AuthenticatedHttpService
			.post<SuccessEnvelope<any>>(
				`/api/kcas/profile/profile/update-email/${nonce}`,
			);
	}

	public static deleteLinkedAccount(id: string): Promise<{ message: string }> {
		return AuthenticatedHttpService.delete<SuccessEnvelope<{ message: string }>>(
			`/api/kloans-iav/linked-accounts/${id}`,
		).then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	public static getAccounts(): Promise<ProfileAccount[]> {
		return AuthenticatedHttpService
			.get<SuccessEnvelope<ProfileAccount[]>>('/api/v1/loan-list')
			.then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	public static getBankAccountsByProduct(
		product: AccountType,
	): Promise<ProfileLinkedAccount[]> {
		return AuthenticatedHttpService.get<SuccessEnvelope<ProfileLinkedAccount[]>>(
			`/api/v1/bank-account?product=${product}`,
		).then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	public static triggerEmailChange(email: string): Promise<void> {
		return AuthenticatedHttpService.post<void, { email: string }>(
			'/api/kloans-consumer/trigger-email-change',
			{ email },
		);
	}

	public static validatePin(pin: string): Promise<{ success: boolean }> {
		return AuthenticatedHttpService.get<SuccessEnvelope<{ success: boolean }>>(
			`/api/kloans-consumer/pin/${pin}`,
		).then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	public static updatePhone(phone: string): Promise<string> {
		return AuthenticatedHttpService.put<SuccessEnvelope<string>, string>(
			'/api/kcas/profile/profile/update-phone',
			phone,
			{
				headers: {
					'Content-Type': 'application/json',
				},
			},
		).then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	public static verifyPhoneCode(code: ProfileVerifyPhoneRequest['confirmation_code']): Promise<boolean> {
		return AuthenticatedHttpService.post<SuccessEnvelope<boolean>, ProfileVerifyPhoneRequest>(
			'/api/kloans-authentication/consumer-verify-phone',
			{
				access_token: SessionService.getJWT(),
				confirmation_code: code,
			},
		).then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}
}
